import { useState, useEffect, useCallback, useMemo } from 'react'
import { Table, Input, Popover, TablePaginationConfig } from 'antd'
import {
  CaretRightOutlined,
  CaretDownOutlined,
  PlusOutlined,
  MinusOutlined,
  SearchOutlined,
  CloseOutlined,
} from '@ant-design/icons'

import dayjs from 'dayjs'
import MovementsAvailabilitiesAdditionalMenu from './TableComponents/MovementsAvailabilitiesAdditionalMenu'
import CustomSort from '../Generic/CustomSort'
import { TranslationKeys } from '../../Constants/TranslationKeys'
import {
  getCurrencyConversion,
  renderTranslation,
  calculateColumnWidthBasedOnText,
  getCurrencySymbolByPricelist,
} from '../../Helpers/GenericHelpers'
import { setCapitalLetter } from '../../Helpers/StringHelpers'
import {
  Causal,
  Quantity,
  DateHeader,
  HAStoreName,
  Sizes,
  ExtraLogs,
  EnableMovementLog,
  EditDate,
  EditDateColLength,
  HandlingExpandColLength,
  SizesColLength,
  UserDetail,
  DocumentationNumber,
  CausalColLength,
  QuantityColLength,
  DateHeaderColLength,
  DocumentationNumberColLength,
  MovementAvailabilitiesDefaultColLength,
  DetailExpandColLength,
  HAStoreNameLogColLength,
  DateFormat,
  TimeFormat,
  OperationId,
  OperationIdColLength,
  HAStoreNameExtraLogColLength,
  VariantModelValue,
  AvailTableColumnTranslationPrefix,
} from '../../Constants/Configuration'
import { TableSortingAscend, TableSortingDescend } from '../../Constants/TableConfiguration'
import { setSortInfo } from '../../Slices/Kiosk/MovementsAvailabilitiesSlice'
import { retrieveArticleDetailMovementsAndAvailabilites } from '../../Services/Kiosk/ArticleServices'
import NumericCell from '../Generic/NumericCell'
import { retrieveSelectedArticleKey } from '../../Helpers/ArticlesHelper'
import { useAppDispatch, useAppSelector } from '../../hooks'
import { customSortTableData, filterDataByCausalAndStore } from 'Helpers/FilterHelper'
import { mapSizeColumnData } from 'Helpers/TableHelper'

function Movements() {
  const dispatch = useAppDispatch()
  const language = useAppSelector(state => state.present.language.value)
  const dataSource = useAppSelector(state => state.present.articleDetail.articleSelected.movements)
  const rowKeysSelected = useAppSelector(state => state.present.article.rowKeysSelected)
  const tableColumns = useAppSelector(state => state.present.movementsAvailabilities.value.movementsColumns)
  const updateLogTableColumns = useAppSelector(
    state => state.present.movementsAvailabilities.value.movementsUpdateTableColumns,
  )
  const availabilitiesFilter = useAppSelector(state => state.present.movementsAvailabilities.value.availabilitiesFilter)
  const sortInfo = useAppSelector(state => state.present.movementsAvailabilities.value.sortInfo)
  const currentCausal = useAppSelector(state => state.present.movementsAvailabilities.value.filterCausal)
  const priceList1 = useAppSelector(state => state.present.tableOptions.priceList1)
  const movementLogVisibile = useAppSelector(state => state.present.configuration.isMovementLogVisible)
  const isPurchasePriceVisibile = useAppSelector(state => state.present.configuration.isPurchasePriceVisibile)
  const [isDetailsExpand, setIsDetailsExpand] = useState(false)
  const [searchValue, setSearchValue] = useState('')

  const articleKey: any = retrieveSelectedArticleKey(rowKeysSelected)

  useEffect(() => {
    setIsDetailsExpand(false)
  }, [articleKey])

  const toggleDetailsColumns = useCallback(() => {
    setIsDetailsExpand(!isDetailsExpand)
  }, [isDetailsExpand])

  const handleUpdateTableData = useCallback(() => {
    retrieveArticleDetailMovementsAndAvailabilites(articleKey, dispatch)
  }, [articleKey, dispatch])

  const setAscendSorting = useCallback(
    sortColumn => {
      dispatch(setSortInfo({ order: TableSortingAscend, columnKey: sortColumn }))
    },
    [dispatch],
  )

  const setDescendSorting = useCallback(
    sortColumn => {
      dispatch(setSortInfo({ order: TableSortingDescend, columnKey: sortColumn }))
    },
    [dispatch],
  )

  const resetSortOrder = useCallback(() => {
    dispatch(setSortInfo({}))
  }, [dispatch])

  const isEnableMovementLogActive = useMemo(() => {
    return availabilitiesFilter.filter(filter => filter.value === EnableMovementLog && filter.active).length !== 0
  }, [availabilitiesFilter])

  const getColumnWidth = useCallback(
    curr => {
      if (isDetailsExpand) {
        if (curr.width) {
          return curr.width
        }
      } else {
        return curr.width
          ? curr.width
          : curr.className === 'column-no-border'
          ? DetailExpandColLength
          : MovementAvailabilitiesDefaultColLength
      }
    },
    [isDetailsExpand],
  )

  const rowExpandable = useCallback(record => {
    return record.firstLog && Object.keys(record.firstLog).length !== 0 ? true : false
  }, [])

  const extraLogRowExpandable = useCallback(record => {
    return record.extraLogs && record.extraLogs.length !== 0 ? true : false
  }, [])

  const renderedColumns = useMemo(() => {
    const columnsToRender = []
    if (tableColumns.length > 0) {
      tableColumns.forEach(element => {
        if (element.key === Causal) {
          columnsToRender.push({
            title: (
              <MovementsAvailabilitiesAdditionalMenu update={handleUpdateTableData}>
                <div>
                  {renderTranslation(
                    language,
                    TranslationKeys[AvailTableColumnTranslationPrefix + setCapitalLetter(element.key)],
                  )}
                </div>
              </MovementsAvailabilitiesAdditionalMenu>
            ),
            dataIndex: element.key,
            key: element.key,
            width: CausalColLength,
            render: (text, record, index) => <div className=''>{text}</div>,
            sortOrder: sortInfo.columnKey && sortInfo.columnKey === element.key ? sortInfo.order : null,
            className: 'causal-column',
          })
        } else if (element.key === Quantity) {
          columnsToRender.push({
            title: (
              <div className='custom-sort-head'>
                <span>
                  {renderTranslation(
                    language,
                    TranslationKeys[AvailTableColumnTranslationPrefix + setCapitalLetter(element.key)],
                  )}
                </span>
                <CustomSort
                  sortInfo={sortInfo}
                  sortColumn={element.key}
                  setAscendSorting={setAscendSorting}
                  setDescendSorting={setDescendSorting}
                  resetSortOrder={resetSortOrder}
                />
              </div>
            ),
            dataIndex: element.key,
            key: element.key,
            width: QuantityColLength,
            sortOrder: sortInfo.columnKey && sortInfo.columnKey === element.key ? sortInfo.order : null,
            align: 'center',
            className: 'noedit ',
            render: text => <NumericCell value={text} highlightNegative />,
          })
        } else if (element.key === DateHeader) {
          columnsToRender.push({
            title: (
              <div className='custom-sort-head'>
                <span>
                  {renderTranslation(
                    language,
                    TranslationKeys[AvailTableColumnTranslationPrefix + setCapitalLetter(element.key)],
                  )}
                </span>
                <CustomSort
                  sortInfo={sortInfo}
                  sortColumn={element.key}
                  setAscendSorting={setAscendSorting}
                  setDescendSorting={setDescendSorting}
                  resetSortOrder={resetSortOrder}
                />
              </div>
            ),
            dataIndex: element.key,
            key: element.key,
            width: DateHeaderColLength,
            render: (text, record, index) => <div className=''>{text}</div>,
            sortOrder: sortInfo.columnKey && sortInfo.columnKey === element.key ? sortInfo.order : null,
            className: 'noedit',
          })
        } else {
          columnsToRender.push({
            title:
              element.key === HAStoreName ? (
                <div className='custom-sort-head'>
                  <span className='availabilities-input-container'>
                    <Input
                      variant={'borderless'}
                      placeholder={renderTranslation(
                        language,
                        TranslationKeys[AvailTableColumnTranslationPrefix + setCapitalLetter(element.key)],
                      )}
                      addonBefore={<SearchOutlined className='input-search-icon' />}
                      className='availabilities-search'
                      value={searchValue}
                      onChange={e => {
                        setSearchValue(e.target.value)
                      }}
                      onClick={e => {
                        e.stopPropagation()
                      }}
                      allowClear={{ clearIcon: <CloseOutlined className='general-icon input-clear-icon' /> }}
                    />
                  </span>
                  <CustomSort
                    sortInfo={sortInfo}
                    sortColumn={element.key}
                    setAscendSorting={setAscendSorting}
                    setDescendSorting={setDescendSorting}
                    resetSortOrder={resetSortOrder}
                  />
                </div>
              ) : (
                <div className='custom-sort-head'>
                  <span>
                    {renderTranslation(
                      language,
                      TranslationKeys[AvailTableColumnTranslationPrefix + setCapitalLetter(element.key)],
                    )}
                  </span>
                  <CustomSort
                    sortInfo={sortInfo}
                    sortColumn={element.key}
                    setAscendSorting={setAscendSorting}
                    setDescendSorting={setDescendSorting}
                    resetSortOrder={resetSortOrder}
                  />
                </div>
              ),
            dataIndex: element.key,
            key: element.key,
            width:
              element.key === DocumentationNumber
                ? DocumentationNumberColLength
                : MovementAvailabilitiesDefaultColLength,
            render: (text, record, index) => (
              <div className={element.key === HAStoreName ? 'store-label' : ''}>{text}</div>
            ),
            sortOrder: sortInfo.columnKey && sortInfo.columnKey === element.key ? sortInfo.order : null,
            className: 'noedit',
          })
        }
      })

      if (!isDetailsExpand) {
        columnsToRender.push({
          title: (
            <div onClick={toggleDetailsColumns} className='cursor-pointer'>
              <PlusOutlined /> {renderTranslation(language, TranslationKeys.tableAvailabilitiesDetails)}
            </div>
          ),
          dataIndex: 'detailColumn',
          key: 'detailColumn',
          width: DetailExpandColLength,
          align: 'center',
          className: 'column-no-border',
        })
      }

      if (isDetailsExpand) {
        const currencySymbol = getCurrencySymbolByPricelist(priceList1, VariantModelValue)
        const sizes = dataSource
          .reduce((prev, curr) => {
            return curr.sizes ? prev.concat(curr.sizes) : prev
          }, [])
          .sort((a, b) => a.order - b.order)
        sizes.forEach(size => {
          if (columnsToRender.filter(column => column.key === 'detail' + size.name).length === 0) {
            columnsToRender.push({
              title: size.name,
              dataIndex: 'detail' + size.name,
              key: 'detail' + size.name,
              width: calculateColumnWidthBasedOnText(size.name, 40),
              render: (text, record, index) => {
                const recordSize = record.sizes.filter(x => x.name === size.name)
                const sizePrice = recordSize.length !== 0 ? recordSize[0].price : null
                if (sizePrice && isPurchasePriceVisibile) {
                  return (
                    <Popover
                      id='movements-size-detail-popover'
                      content={currencySymbol + ' ' + getCurrencyConversion(sizePrice)}
                      trigger='hover'
                    >
                      <div>
                        <NumericCell value={text} highlightNegative />
                      </div>
                    </Popover>
                  )
                } else {
                  return <NumericCell value={text} highlightNegative />
                }
              },
              align: 'center',
              className: 'detail-size noedit',
            })
          }
        })

        columnsToRender.push({
          title: (
            <div onClick={toggleDetailsColumns} className='cursor-pointer'>
              <MinusOutlined /> {renderTranslation(language, TranslationKeys.tableAvailabilitiesDetails)}
            </div>
          ),
          dataIndex: 'detailColumn',
          key: 'detailColumn',
          width: DetailExpandColLength,
          align: 'center',
          className: 'column-no-border',
        })
      }
    }
    return columnsToRender
  }, [
    dataSource,
    handleUpdateTableData,
    isDetailsExpand,
    language,
    priceList1,
    resetSortOrder,
    searchValue,
    setAscendSorting,
    setDescendSorting,
    sortInfo,
    tableColumns,
    toggleDetailsColumns,
    isPurchasePriceVisibile,
  ])

  const results = useMemo(() => {
    return dataSource && dataSource.length > 0 ? dataSource : []
  }, [dataSource])

  const filterResults = useCallback(
    results => {
      return filterDataByCausalAndStore(results, currentCausal, searchValue)
    },
    [currentCausal, searchValue],
  )

  const sortedData = useMemo(() => {
    const resultsToDisplay = mapSizeColumnData(filterResults(results))

    return customSortTableData(resultsToDisplay, sortInfo)
  }, [filterResults, results, sortInfo])

  const expandedExtraLogRowRender = useCallback(
    (record, index, indent, expanded) => {
      const extraLogColumns = []
      const extraLogData = []

      if (updateLogTableColumns.length > 0) {
        updateLogTableColumns.forEach(element => {
          if (element.key === Quantity) {
            extraLogColumns.push({
              title: element.key,
              dataIndex: element.key,
              key: element.key,
              width: 80,
              className: 'extra-log-quantity-col ',
              render: text => <NumericCell value={text} />,
            })
          } else {
            if (
              element.key !== EditDate &&
              element.key !== UserDetail &&
              element.key !== Sizes &&
              element.key !== OperationId
            ) {
              extraLogColumns.push({
                title: element.key,
                dataIndex: element.key,
                key: element.key,
                width:
                  element.key === HAStoreName
                    ? HAStoreNameExtraLogColLength
                    : element.key === DateHeader
                    ? DateHeaderColLength
                    : element.key === Causal
                    ? CausalColLength
                    : element.key === DocumentationNumber
                    ? DocumentationNumberColLength
                    : MovementAvailabilitiesDefaultColLength,
                render: (text, record, index) => (
                  <div className={element.key === HAStoreName ? 'store-label' : ''}>{text ? text : ''}</div>
                ),
                className: '',
              })
            }

            if (isDetailsExpand) {
              if (element.key === Sizes) {
                extraLogColumns.push({
                  title: element.key,
                  dataIndex: element.key,
                  key: element.key,
                  width: SizesColLength,
                  render: (text, record, index) => (
                    <div>
                      {text && text.length !== 0
                        ? renderTranslation(
                            language,
                            TranslationKeys[AvailTableColumnTranslationPrefix + setCapitalLetter(element.key)],
                          ) +
                          ': ' +
                          text.join(', ')
                        : ''}
                    </div>
                  ),
                  className: '',
                })
              } else if (element.key === EditDate || element.key === UserDetail || element.key === OperationId) {
                extraLogColumns.push({
                  title: element.key,
                  dataIndex: element.key,
                  key: element.key,
                  width:
                    element.key === EditDate
                      ? EditDateColLength
                      : element.key === OperationId
                      ? OperationIdColLength
                      : MovementAvailabilitiesDefaultColLength,
                  render: (text, record, index) => (
                    <div>
                      {(element.key === EditDate || element.key === UserDetail
                        ? renderTranslation(
                            language,
                            TranslationKeys[AvailTableColumnTranslationPrefix + setCapitalLetter(element.key)],
                          ) + ': '
                        : '') +
                        (element.key === EditDate
                          ? dayjs(text).format(DateFormat + ' ' + TimeFormat)
                          : text
                          ? text
                          : '')}
                    </div>
                  ),
                  className: '',
                })
              }
            }
          }
        })
      }

      if (record.extraLogs && record.extraLogs.length !== 0) {
        record.extraLogs.forEach((extraLog, index) => {
          const logToAdd = { ...extraLog }
          logToAdd['rowId'] = record.teId + '-' + index
          extraLogData.push(logToAdd)
        })
      } else {
        extraLogData.push({})
      }

      const width =
        extraLogData.reduce((prev, curr) => {
          return prev + getColumnWidth(curr)
        }, 0) + HandlingExpandColLength

      return (
        <Table
          rowKey={record => 'extra-log-' + record.rowId}
          id='movementsExtraLogHandlingTable'
          tableLayout='fixed'
          columns={extraLogColumns}
          dataSource={extraLogData}
          rowClassName={(record, index) => {
            if (movementLogVisibile) {
              // if (record.deleted) {
              //     return "deleted";
              // }
              // if (record.differences && record.differences.length !== 0) {
              //     return "fields-changes";
              // }
              return ''
            }
          }}
          pagination={false}
          showHeader={false}
          style={{ width: width }}
          size='small'
        />
      )
    },
    [getColumnWidth, isDetailsExpand, language, movementLogVisibile, updateLogTableColumns],
  )

  const expandedLogRowRender = useCallback(
    (record, index, indent, expanded) => {
      const logColumns = []
      const logData = []

      if (updateLogTableColumns.length > 0) {
        updateLogTableColumns.forEach(element => {
          if (element.key === Quantity) {
            logColumns.push({
              title: element.key,
              dataIndex: element.key,
              key: element.key,
              width: QuantityColLength,
              className: 'extra-log-quantity-col ',
              render: text => <NumericCell value={text} />,
            })
          } else {
            if (
              element.key !== EditDate &&
              element.key !== UserDetail &&
              element.key !== Sizes &&
              element.key !== OperationId
            ) {
              logColumns.push({
                title: element.key,
                dataIndex: element.key,
                key: element.key,
                width:
                  element.key === HAStoreName
                    ? HAStoreNameLogColLength
                    : element.key === DateHeader
                    ? DateHeaderColLength
                    : element.key === Causal
                    ? CausalColLength
                    : element.key === DocumentationNumber
                    ? DocumentationNumberColLength
                    : MovementAvailabilitiesDefaultColLength,
                render: (text, record, index) => (
                  <div className={element.key === HAStoreName ? 'store-label' : ''}>{text ? text : ''}</div>
                ),
                className: '',
              })
            }

            if (isDetailsExpand) {
              if (element.key === Sizes) {
                logColumns.push({
                  title: element.key,
                  dataIndex: element.key,
                  key: element.key,
                  width: SizesColLength,
                  render: (text, record, index) => (
                    <div>
                      {text && text.length !== 0
                        ? renderTranslation(
                            language,
                            TranslationKeys[AvailTableColumnTranslationPrefix + setCapitalLetter(element.key)],
                          ) +
                          ': ' +
                          text.join(', ')
                        : ''}
                    </div>
                  ),
                  className: '',
                })
              } else if (element.key === EditDate || element.key === UserDetail || element.key === OperationId) {
                logColumns.push({
                  title: element.key,
                  dataIndex: element.key,
                  key: element.key,
                  width:
                    element.key === EditDate
                      ? EditDateColLength
                      : element.key === OperationId
                      ? OperationIdColLength
                      : MovementAvailabilitiesDefaultColLength,
                  render: (text, record, index) => (
                    <div>
                      {(element.key === EditDate || element.key === UserDetail
                        ? renderTranslation(
                            language,
                            TranslationKeys[AvailTableColumnTranslationPrefix + setCapitalLetter(element.key)],
                          ) + ': '
                        : '') +
                        (element.key === EditDate
                          ? dayjs(text).format(DateFormat + ' ' + TimeFormat)
                          : text
                          ? text
                          : '')}
                    </div>
                  ),
                  className: '',
                })
              }
            }
          }
        })
      }

      if (record.firstLog && Object.keys(record.firstLog).length !== 0) {
        let objectToAdd = Object.create(record.firstLog)
        if (record.extraLogs && record.extraLogs.length !== 0) {
          objectToAdd[ExtraLogs] = record.extraLogs
        }
        objectToAdd['teId'] = record.teId
        logData.push(objectToAdd)
      } else {
        logData.push({})
      }

      const width =
        logColumns.reduce((prev, curr) => {
          return prev + getColumnWidth(curr)
        }, 0) + HandlingExpandColLength

      return (
        <Table
          rowKey={record => 'first-log-' + record.teId}
          id='movementsFirstLogHandlingTable'
          tableLayout='fixed'
          columns={logColumns}
          dataSource={logData}
          rowClassName={(record, index) => {
            // if(movementLogVisibile) {
            //     if (record.deleted) {
            //         return "deleted";
            //     }
            //     if (record.differences && record.differences.length !== 0) {
            //         return "fields-changes";
            //     }
            // }
            return ''
          }}
          pagination={false}
          showHeader={false}
          expandable={{
            expandedRowRender: expandedExtraLogRowRender,
            rowExpandable: extraLogRowExpandable,
            expandIcon: ({ expanded, onExpand, record }) =>
              record.extraLogs && record.extraLogs.length !== 0 ? (
                expanded ? (
                  <MinusOutlined
                    onClick={e => onExpand(record, e)}
                    className='availabilities-minus-icon expand-element-icon'
                  />
                ) : (
                  <PlusOutlined
                    onClick={e => onExpand(record, e)}
                    className='availabilities-plus-icon expand-element-icon'
                  />
                )
              ) : null,
            columnWidth: HandlingExpandColLength,
          }}
          style={{ width: width }}
          size='small'
        />
      )
    },
    [
      language,
      isDetailsExpand,
      updateLogTableColumns,
      expandedExtraLogRowRender,
      getColumnWidth,
      extraLogRowExpandable,
    ],
  )

  const getFirstColumWidthIfIsSet = useMemo(() => {
    if (isEnableMovementLogActive) {
      const tableElement = document.getElementById('movementsTable')
      if (tableElement) {
        const expandRowElement = document
          .getElementById('movementsTable')
          .getElementsByClassName('ant-table-row-expand-icon-cell')

        return expandRowElement && expandRowElement.length !== 0
          ? (expandRowElement[0] as HTMLElement).offsetWidth
          : HandlingExpandColLength
      }

      return HandlingExpandColLength
    }

    return 0
  }, [isEnableMovementLogActive])

  const width = useMemo(() => {
    return (
      renderedColumns.reduce((prev, curr) => {
        return prev + getColumnWidth(curr)
      }, 0) + getFirstColumWidthIfIsSet
    )
  }, [getColumnWidth, getFirstColumWidthIfIsSet, renderedColumns])

  const paginationInfo = useMemo(() => {
    return {
      position: ['bottomRight'],
      total: sortedData.length,
      showSizeChanger: true,
    } as TablePaginationConfig
  }, [sortedData.length])

  return (
    <Table
      rowKey={record => record.teId}
      id='movementsTable'
      columns={renderedColumns}
      tableLayout='fixed'
      dataSource={sortedData}
      expandable={
        isEnableMovementLogActive
          ? {
              expandedRowRender: expandedLogRowRender,
              rowExpandable: rowExpandable,
              expandIcon: ({ expanded, onExpand, record }) =>
                record.firstLog && Object.keys(record.firstLog).length !== 0 ? (
                  expanded ? (
                    <CaretDownOutlined
                      onClick={e => onExpand(record, e)}
                      className='movement-caret-down caret-icon-down'
                    />
                  ) : (
                    <CaretRightOutlined
                      onClick={e => onExpand(record, e)}
                      className='movement-caret-right caret-icon-right'
                    />
                  )
                ) : null,
              columnWidth: HandlingExpandColLength,
            }
          : null
      }
      rowClassName={(record, index) => {
        // if(movementLogVisibile) {
        //     if (record.deleted) {
        //         return "deleted";
        //     }
        //     if (record.added) {
        //         return "added";
        //     }
        // }
        return ''
      }}
      pagination={paginationInfo}
      showSorterTooltip={false}
      style={{ width: width }}
      size='small'
    />
  )
}

export default Movements
